import { graphql } from 'gatsby';
import React, { useState } from 'react';
import { Box, Heading, Icon, IconType } from 'lucid-components-react';
import ReactHtmlParser from 'react-html-parser';

import { Text } from 'theme/typography';
import { mqMin } from 'theme/base';
import { LucidTheme } from 'types/types';
import {
  Drupal_ParagraphFlexiLeaders,
  Drupal_ParagraphFlexiLeadershipBio
} from 'generated/graphql';
import { getProcessedValue } from 'utils/helpers';
import linkParser from 'utils/link-parser';
import Link from 'components/link/link';
import xSvg from 'assets/x.svg';

interface Props {
  entity: Drupal_ParagraphFlexiLeaders;
  showMore: string;
  showLess: string;
  profiles?: string;
}

type Leadership = {
  name: string;
  role: string;
  paragraph: { processed?: string | null };
  image: string;
  social?: any[];
};

const BigLeadership = ({
  name,
  role,
  paragraph,
  image,
  social
}: Leadership) =>
  (
    <Box
      key={name}
      css={(theme: LucidTheme) => ({
        gridColumn: '1',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gridTemplateRows: 'repeat(1, auto)',
        display: 'grid',
        gridGap: theme.space[4],
        alignItems: 'center',
        marginTop: theme.space[9],
        [mqMin.tabletLandscape]: {
          gridColumn: '3 / 10',
          justifySelf: 'center',
          gridTemplateColumns: 'repeat(2, 50%)'
        }
      })}
    >
      <Box
        css={(theme: LucidTheme) => ({
          gridColumn: '1',
          justifySelf: 'center',
          backgroundColor: theme.colors.coolGrey[5],
          borderRadius: '50%',
          overflow: 'hidden',
          boxShadow: `inset 0px 0px 0px 2px ${theme.colors.coolGrey[10]}, inset 0px 0px 0px 10px ${theme.colors.white}`,
          height: '280px',
          width: '280px',
          alignItems: 'center',
          [mqMin.tabletLandscape]: {
            gridColumn: '1 / 2',
            height: '300px',
            width: '300px'
          }
        })}
      >
        <img alt={name} src={image} />
      </Box>
      <Box
        css={{
          gridColumn: '1',
          justifySelf: 'center',
          [mqMin.tabletLandscape]: {
            gridColumn: '2 / 3'
          }
        }}
      >
        <Heading
          as="h2"
          css={(theme: LucidTheme) => ({
            color: theme.colors.primary
          })}
          variant="subheadXS700"
        >
          {name}
        </Heading>
        <Text textStyles="introXS300">{role}</Text>
        {social && social.length > 0 && (
          <Box
            alignItems="center"
            css={(theme: LucidTheme) => ({
              marginTop: theme.space[2],
              marginBottom: theme.space[2]
            })}
            display="flex"
          >
            <Text
              css={(theme: LucidTheme) => ({
                flex: '0 1 20%',
                maxWidth: 'fit-content',
                marginRight: theme.space[4],
                marginTop: '0',
                marginBottom: '0'
              })}
              textStyles="bodyS400"
            >
              Profiles:
            </Text>
            {social.map(
              ({
                entity: {
                  fieldSocialProfileUrl,
                  fieldFlexiSeoImage: {
                    entity: { fieldAltText, fieldImageLink }
                  }
                }
              }) =>
                (
                  <Link
                    key={fieldSocialProfileUrl}
                    css={(theme: LucidTheme) => ({ marginRight: theme.space[4] })}
                    to={fieldSocialProfileUrl}
                  >
                    <img
                      alt={fieldAltText}
                      css={{ height: '20px' }}
                      src={fieldImageLink}
                    />
                  </Link>
                )
            )}
          </Box>
        )}

        <Text as="div" textStyles="bodyS400">
          {ReactHtmlParser(getProcessedValue(paragraph), {
            transform: linkParser
          })}
        </Text>
      </Box>
    </Box>
  );

const LittleLeadership = ({ name, role, paragraph, image }: Leadership) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <Box
      key={name}
      css={(theme: LucidTheme) => ({
        textAlign: 'center',
        gridColumn: '1 / 5',
        width: '100%',
        overflow: 'hidden',
        overflowY: 'scroll',
        marginX: theme.space[4],
        '&:hover': {
          cursor: 'pointer'
        },
        [mqMin.tabletLandscape]: {
          gridColumn: 'auto',
          height: '350px'
        }
      })}
      onClick={() => setIsHovering(!isHovering)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {!isHovering ? (
        <Box
          css={{
            justifySelf: 'center'
          }}
        >
          <Box
            css={(theme: LucidTheme) => ({
              justifySelf: 'center',
              backgroundColor: theme.colors.coolGrey[5],
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: `inset 0px 0px 0px 2px ${theme.colors.coolGrey[10]}, inset 0px 0px 0px 10px ${theme.colors.white}`,
              height: '180px',
              width: '180px',
              alignItems: 'center',
              margin: 'auto',
              [mqMin.tabletSmall]: {
                gridColumn: '1 / 2',
                height: '200px',
                width: '200px'
              }
            })}
          >
            <img alt={name} src={image} />
          </Box>
          <Heading
            as="h2"
            css={(theme: LucidTheme) => ({
              color: theme.colors.primary
            })}
            variant="subheadXS700"
          >
            {name}
          </Heading>
          <Text textStyles="introXS300">{role}</Text>
          <Box>
            <Text
              css={(theme: LucidTheme) => ({
                color: theme.colors.lucidCoBlue,
                [mqMin.tabletSmall]: {
                  display: 'none'
                }
              })}
              textStyles="linkS600"
            >
              Bio
              <Icon
                css={(theme: LucidTheme) => ({
                  color: theme.colors.lucidCoBlue,
                  marginLeft: theme.space[2]
                })}
                type={IconType.ArrowRight}
              />
            </Text>
          </Box>
        </Box>
      ) :
        (
          <Text
            as="div"
            css={(theme: LucidTheme) => ({
              background: theme.colors.coolGrey[0],
              padding: `${theme.space[7]}px ${theme.space[6]}px`,
              margin: '0',
              textAlign: 'left',
              '> p': {
                margin: '0'
              }
            })}
            textStyles="bodyS400"
          >
            {ReactHtmlParser(getProcessedValue(paragraph), {
              transform: linkParser
            })}
            <Box
              key={name}
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                [mqMin.tabletSmall]: {
                  display: 'none'
                }
              }}
              mt={4}
            >
              <img alt="exit" src={xSvg} width="24" />
              <Text
                css={(theme: LucidTheme) => ({
                  color: theme.colors.primary
                })}
                textStyles="linkS500"
              >
                Close
              </Text>
            </Box>
          </Text>
        )}
    </Box>
  );
};

const leadersList = (
  leaders: Drupal_ParagraphFlexiLeadershipBio[] | any[],
  big: boolean
) => {
  const lead = leaders.filter(({ entity }: any) => {
    if (entity.fieldFlexiLeadershipBig === big) {
      return entity;
    }
  });
  return lead;
};

const FlexiLeadershipBios = ({
  entity: { fieldIndividualLeaders },
  showMore,
  showLess
}: Props) => {
  const itemsPerTwoRows = 6;
  const [itemsToShow, setItemsToShow] = useState(6);
  const bigWigs: Drupal_ParagraphFlexiLeadershipBio[] | null | undefined =
    fieldIndividualLeaders && leadersList(fieldIndividualLeaders, true);
  const littleWigs: Drupal_ParagraphFlexiLeadershipBio[] | null | undefined =
    fieldIndividualLeaders && leadersList(fieldIndividualLeaders, false);

  return (
    <Box
      css={{
        width: '100%'
      }}
      gridColumn={'1 / -1'}
    >
      <Box
        css={{
          label: 'content',
          display: 'grid',
          gridTemplateColumns: 'repeat(1, 1fr)',
          [mqMin.tabletLandscape]: {
            flexDirection: 'row',
            gridTemplateColumns: 'repeat(12, 8.33%)'
          }
        }}
        gridColumn={'1 / -1'}
      >
        {bigWigs?.map(
          ({
            entity: {
              fieldFlexiLeadershipName,
              fieldFlexiLeadershipRole,
              fieldFlexiLongText,
              fieldFlexiImage,
              fieldSocialProfiles
            }
          }: any) =>
            (
              <BigLeadership
                key={fieldFlexiLeadershipName}
                image={fieldFlexiImage}
                name={fieldFlexiLeadershipName}
                paragraph={fieldFlexiLongText}
                role={fieldFlexiLeadershipRole}
                social={fieldSocialProfiles}
              />
            )
        )}
      </Box>
      <Box
        css={(theme: LucidTheme) => ({
          label: 'content',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gridTemplateRows: 'repeat(1, auto)',
          width: '100%',
          gridGap: theme.space[5],
          marginTop: theme.space[10],
          [mqMin.tabletLandscape]: {
            flexDirection: 'row',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(1, auto)'
          }
        })}
        gridColumn={'1 / 7'}
      >
        {littleWigs
          ?.slice(0, itemsToShow)
          .map(
            ({
              entity: {
                fieldFlexiLeadershipName,
                fieldFlexiLeadershipRole,
                fieldFlexiLongText,
                fieldFlexiImage
              }
            }: any) =>
              (
                <LittleLeadership
                  key={fieldFlexiLeadershipName}
                  image={fieldFlexiImage}
                  name={fieldFlexiLeadershipName}
                  paragraph={fieldFlexiLongText}
                  role={fieldFlexiLeadershipRole}
                />
              )
          )}
      </Box>
      <Box
        css={(theme: LucidTheme) => ({
          label: 'content',
          textAlign: 'center',
          marginBottom: theme.space[6],
          [mqMin.tabletLandscape]: {}
        })}
        gridColumn={'1 / -1'}
      >
        {littleWigs && littleWigs.length > itemsToShow ? (
          <Box
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              '&:hover': { cursor: 'pointer' }
            }}
            onClick={() => setItemsToShow(itemsToShow + itemsPerTwoRows)}
          >
            <Text
              css={(theme: LucidTheme) => ({
                color: theme.colors.primary,
                flex: '0 1 100%',
                width: '100%',
                marginTop: theme.space[7],
                '&:hover': { cursor: 'pointer' }
              })}
              textStyles="subheadXXS700"
            >
              <Box
                css={(theme: LucidTheme) => ({
                  marginTop: theme.space[3]
                })}
              >
                {showMore}
              </Box>
              <Icon
                css={(theme: LucidTheme) => ({
                  marginTop: theme.space[3],
                  marginBottom: theme.space[3]
                })}
                type={IconType.CarotDown}
              />
            </Text>
          </Box>
        ) :
          (
            <Box
              css={{
                display: 'flex',
                flexWrap: 'wrap',
                '&:hover': { cursor: 'pointer' }
              }}
              onClick={() => setItemsToShow(itemsPerTwoRows)}
            >
              <Text
                css={(theme: LucidTheme) => ({
                  color: theme.colors.primary,
                  flex: '0 1 100%',
                  width: '100%',
                  marginTop: theme.space[7],
                  '&:hover': { cursor: 'pointer' }
                })}
                textStyles="subheadXXS700"
              >
                <Icon
                  css={(theme: LucidTheme) => ({
                    transform: 'rotate(180deg)',
                    marginTop: theme.space[3],
                    marginBottom: theme.space[3]
                  })}
                  type={IconType.CarotDown}
                />
                <Box>{showLess}</Box>
              </Text>
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default FlexiLeadershipBios;

export const query = graphql`
  fragment flexiLeadership on Drupal_ParagraphFlexiLeaders {
    fieldIndividualLeaders {
      entity {
        ... on Drupal_ParagraphFlexiLeadershipBio {
          fieldFlexiLongText {
            processed
          }
          fieldFlexiLeadershipRole
          fieldFlexiLeadershipName
          fieldFlexiLeadershipBig
          fieldFlexiImage
          fieldSocialProfiles {
            entity {
              ... on Drupal_ParagraphFlexiSocialProfiles {
                fieldSocialProfileUrl
                fieldFlexiSeoImage {
                  entity {
                    ... on Drupal_ParagraphSeoImage {
                      fieldAltText
                      fieldImageLink
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
